/**
 * Template edit-in-place — React port of the legacy real-template editor:
 * loads REAL email HTML (a pulled gallery template or a delivered sent email)
 * into an iframe and gives every section the same chrome the block editor has
 * (hover badge + move/duplicate/delete/add controls) plus inline text editing.
 *
 * LOAD-BEARING RULES carried over from production (see MIGRATION.md gotchas):
 *  1. srcset beats src — swapping an image must strip srcset/sizes and any
 *     <picture><source> siblings, or the client keeps loading the old asset.
 *  2. Table-context-safe swaps — replacing a <tr>-slot with a <div> renders in
 *     the live DOM but gets foster-parented OUT of the table on ANY re-parse
 *     (undo/preview/submit). Row slots get a <tr><td> wrapper parsed in a
 *     <tbody> scratch (legacy __dynExtSwap).
 *  3. Template fidelity — inline editing changes text/attributes, never
 *     restructures the template's HTML.
 *
 * The in-iframe editor script is the legacy __esEditorFn approach: a
 * self-contained script injected before </body> that labels the module stack,
 * draws the hover chrome, and posts edits up to the parent.
 */
import { useEffect, useRef, type MutableRefObject } from "react";

/** a selected template section as the in-frame editor reports it */
export interface TplSection {
  idx: number;
  label: string;
  hero: boolean;
  imgs: { src: string; alt: string }[];
  links: { text: string; href: string }[];
}
/** index-addressed section ops the inspector calls into the frame */
export interface TplEditorApi {
  img: (idx: number, imgIdx: number, url: string, alt?: string) => TplSection | null;
  link: (idx: number, linkIdx: number, field: "text" | "href", val: string) => TplSection | null;
  remove: (idx: number) => void;
  deselect: () => void;
}

interface Props {
  html: string;
  onSerialized: (html: string) => void;
  /** a section was clicked (null = deselected) — drives the TEMPLATE SECTION inspector */
  onSelect?: (sel: TplSection | null) => void;
  /** the parent's handle for section ops */
  apiRef?: MutableRefObject<TplEditorApi | null>;
  /**
   * GUARDRAILS FOR AN OPAQUE DOCUMENT (2026-08-31). Frame label → character cap, from
   * `capsByLabel()`. Absent or empty means no limits, which is what this editor did
   * before — so nothing changes for the paths that do not pass it.
   */
  caps?: Record<string, number>;
  /** lock the artwork: copy stays editable, images do not (see `lockArt`) */
  lockArt?: boolean;
}

/* runs INSIDE the iframe — ES5, dependency-free (port of legacy __esEditorFn) */
const EDITOR_FN = function editorFn(CAPS?: Record<string, number>, LOCK_ART?: boolean) {
  var D = document, B = D.body, W = window as unknown as Record<string, unknown>;
  /* ── THE CAPS LIVE ON THE WINDOW, AND THE LISTENER BINDS ONCE (2026-08-31) ────
     A second `editorFn` call on the same document used to stack a second input
     listener, and the FIRST one — holding the previous call's caps — clamped. In the
     app each iframe document is fresh so it never bit, but "re-run the editor on this
     document" is a legitimate thing to do and stacking handlers is a real defect.
     One handler, reading whatever the latest call put on the window. */
  (W as Record<string, unknown>).__esCaps = CAPS || {};
  (W as Record<string, unknown>).__esLockArt = !!LOCK_ART;
  var _lockArt = !!LOCK_ART;
  function caps(): Record<string, number> {
    return ((W as Record<string, unknown>).__esCaps as Record<string, number>) || {};
  }
  try { B.setAttribute("contenteditable", "true"); } catch (e) { /* noop */ }
  try { B.style.position = B.style.position || "relative"; } catch (e) { /* noop */ }
  function blockKids(el: Element) {
    var out: Element[] = [], i, c, rc, tg;
    for (i = 0; i < el.children.length; i++) {
      c = el.children[i]; tg = c.tagName;
      if (c.getAttribute && c.getAttribute("data-esedit")) continue;
      if (tg === "TABLE" || tg === "TR" || tg === "DIV" || tg === "TBODY" || tg === "TD") {
        rc = null; try { rc = c.getBoundingClientRect(); } catch (e) { /* noop */ }
        if (rc && rc.height > 4) out.push(c);
      }
    }
    return out;
  }
  function findContainer() {
    var all = D.querySelectorAll("table,tbody,tr,td,div"), best: Element | null = null, bk: Element[] = [], bs = -1, i, el, kids;
    var bodyH = (D.body && D.body.scrollHeight) || 1;
    for (i = 0; i < all.length; i++) {
      el = all[i]; kids = blockKids(el);
      if (kids.length < 3) continue;
      var tops: number[] = [], bots: number[] = [], j;
      for (j = 0; j < kids.length; j++) { var r = kids[j].getBoundingClientRect(); tops.push(r.top); bots.push(r.bottom); }
      var span = Math.max.apply(null, bots) - Math.min.apply(null, tops);
      if (span < 120) continue;
      var score = (span / bodyH) * 100 + kids.length;
      if (score > bs) { bs = score; best = el; bk = kids; }
    }
    return { el: best, kids: bk };
  }
  var MODCONTAINER: Element | null = null, MODROWS: Element[] = [];
  function scan() {
    var prev = D.querySelectorAll('[data-esmod="1"]');
    for (var p = 0; p < prev.length; p++) { prev[p].removeAttribute("data-esmod"); }
    var kids;
    if (MODCONTAINER && (MODCONTAINER as Element & { isConnected: boolean }).isConnected) kids = blockKids(MODCONTAINER);
    else { var f = findContainer(); MODCONTAINER = f.el; kids = f.kids; }
    MODROWS = kids || [];
    for (var i = 0; i < MODROWS.length; i++) {
      MODROWS[i].setAttribute("data-esmod", "1");
      /* ── STAMP THE LABEL NOW, BEFORE ANY EDITING (2026-08-31) ──────────────────
         The cap is looked up by label, and `labelFor` reads the section's own TEXT to
         decide what it is — so resolving it mid-edit means the text being capped is
         also the input to the lookup. Paste 700 characters into a body block and it
         stops classifying as BODY COPY, the cap resolves to nothing, and the clamp
         lets the paste straight through: the exact case this guards. Stamped here,
         read from the attribute later. */
      try { MODROWS[i].setAttribute("data-esmodlabel", labelFor(MODROWS[i])); } catch (e) { /* noop */ }
    }
    if (_lockArt) lockArt();
  }
  /* ── THE ARTWORK IS NOT A RESEND-TIME DECISION (2026-08-31) ───────────────────
     Jack, on FNO pilot feedback: "There were no character limits and I could change
     icons." Approved artwork is a large part of why a template is approved; copy stays
     editable, images do not. Re-applied on every scan because the document is
     re-rendered from the host and a one-time pass would be lost. */
  function lockArt() {
    try {
      if (!D.getElementById("__esartlock")) {
        var ls = D.createElement("style");
        ls.id = "__esartlock"; ls.setAttribute("data-esedit", "1");
        ls.textContent = "img{pointer-events:none!important;-webkit-user-modify:read-only!important;user-select:none!important}";
        D.head.appendChild(ls);
      }
      var im = D.querySelectorAll("img");
      for (var i = 0; i < im.length; i++) {
        im[i].setAttribute("contenteditable", "false");
        im[i].setAttribute("draggable", "false");
      }
    } catch (e) { /* noop */ }
  }
  /** the cap for a section, from the label stamped at scan — never a fresh classify */
  function capFor(r: Element | null): number {
    if (!r) return 0;
    try {
      var lab = (r.getAttribute && r.getAttribute("data-esmodlabel")) || "";
      if (!lab) lab = labelFor(r);
      return caps()[String(lab).toUpperCase()] || 0;
    } catch (e) { return 0; }
  }
  /** the one-line "you have hit the limit" note, in the document itself */
  function capFlash(msg: string) {
    try {
      var f = D.getElementById("__escapflash");
      if (!f) {
        f = D.createElement("div");
        f.id = "__escapflash";
        f.setAttribute("data-esedit", "1");
        f.setAttribute("contenteditable", "false");
        f.style.cssText = "position:fixed;left:50%;bottom:14px;transform:translateX(-50%);z-index:99999;"
          + "background:#b23b3b;color:#fff;font:700 11.5px/1 -apple-system,Segoe UI,Arial,sans-serif;"
          + "padding:8px 12px;border-radius:7px;pointer-events:none;box-shadow:0 3px 10px rgba(0,0,0,.3)";
        B.appendChild(f);
      }
      f.textContent = msg;
      f.style.opacity = "1";
      clearTimeout((W as Record<string, number>).__escapT);
      (W as Record<string, unknown>).__escapT = setTimeout(function () { try { f!.style.opacity = "0"; } catch (e) { /* noop */ } }, 1600);
    } catch (e) { /* noop */ }
  }
  (W as Record<string, unknown>).__edRescan = scan;
  /* the label of one section, for the host (2026-08-31). Same shape as __edRescan and
     __edSwapImg — a hook the host can call in on. Added so the labeler can be asserted
     against directly: the badge it feeds is a single shared hover overlay, so reading
     chrome is not a way to check what a given section was called. */
  (W as Record<string, unknown>).__edSecLabel = function (el: Element) { return labelFor(el); };
  var st = D.createElement("style");
  st.setAttribute("data-esedit", "1");
  /* chrome CSS = the monolith's own rules VERBATIM (index.html L7350 — .esch /
     .esbadge / .esbtns + hovers; Jack 2026-08-09: "doesn't reflect the actual
     one in terms of the module"). Only [data-essel] (selection outline) is ours. */
  st.textContent = ".esch{position:absolute;pointer-events:none;z-index:99998;box-sizing:border-box;display:none;outline:2px solid #C8A664;outline-offset:-2px;background:rgba(200,166,100,.06)}.esch.on{display:block}.esbadge{position:absolute;top:4px;left:4px;pointer-events:auto;font:800 9.5px/1 -apple-system,Segoe UI,Arial,sans-serif;letter-spacing:.05em;text-transform:uppercase;color:#fff;background:rgba(27,54,93,.94);padding:5px 8px;border-radius:5px;white-space:nowrap}.esbtns{position:absolute;top:4px;right:4px;pointer-events:auto;display:flex;gap:3px}.esbtns button{width:26px;height:26px;border:0;border-radius:6px;background:rgba(27,54,93,.94);color:#fff;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.25)}.esbtns button:hover{background:#1B365D}.esbtns button.del:hover{background:#b23b3b}[data-essel='1']{outline:2px solid #C8A664!important;outline-offset:-2px}";
  D.head.appendChild(st);
  var ch = D.createElement("div");
  ch.className = "esch"; ch.setAttribute("data-esedit", "1"); ch.setAttribute("contenteditable", "false");
  /* monolith template chrome: .esbadge label + move/dup/del — NO "+" adder in
     MVP (the monolith's esaddbtn is MVP-suppressed; customize/move only) */
  ch.innerHTML = '<span class="esbadge"></span><span class="esbtns"><button data-a="up">&#8593;</button><button data-a="down">&#8595;</button><button data-a="dup">&#10697;</button><button data-a="del" class="del">&#10005;</button></span>';
  B.appendChild(ch);
  /* ── section labeler — the monolith's labelFor VERBATIM (index.html L7306-7341;
     the invented heuristics mislabeled 4 of 11 Tease sections in the 2026-08-09
     overlap audit: WE'RE HERE TO HELP / SOCIAL FOOTER / FOOTER ×2) ── */
  function bigImgs(el: Element) { var out: HTMLImageElement[] = [], im = el.querySelectorAll("img"), i; for (i = 0; i < im.length; i++) { var w = im[i].width || parseInt(im[i].getAttribute("width") || "0", 10); if (w >= 20) out.push(im[i] as HTMLImageElement); } return out; }
  function labelFor(r: Element): string {
    var t = ((r as HTMLElement).innerText || "").replace(/\s+/g, " ").trim(), low = t.toLowerCase();
    var imgs = r.getElementsByTagName("img"), links = r.querySelectorAll("a[href]");
    var html = (r.innerHTML || "").toLowerCase(), heads = r.querySelector("h1,h2,h3,h4");
    var i, im, w, h, big: Element | null = null, logo = false;
    for (i = 0; i < imgs.length; i++) { im = imgs[i]; w = im.width || im.clientWidth || 0; h = im.height || im.clientHeight || 0;
      if (w >= 380 || h >= 230) big = big || im;
      var s = ((im.getAttribute("src") || "") + " " + (im.getAttribute("alt") || "")).toLowerCase();
      /* ── THE BRAND MARK IS IDENTIFIED BY WHAT IT IS, NOT WHERE IT SITS (2026-08-31) ──
         Found while tracing SUN's report about moving the hero: the header below was
         relabeled "BUTTON / CTA", because the only thing marking it as the logo header
         was being the FIRST block. Moving the hero above it is a legitimate thing for a
         division to do, so position cannot be the tell. A brand mark is a NARROW image
         that is the Lennar wordmark — by src, by alt, or by the studio module path the
         header art is served from. */
      if (s.indexOf("logo") > -1) logo = true;
      else if (w && w <= 260 && (/\blennar\b/.test(s) || s.indexOf("/modules/studio/") > -1)) logo = true; }
    if (html.indexOf("unsubscribe") > -1 || html.indexOf("member_") > -1 || low.indexOf("view this email") > -1 || low.indexOf("all rights reserved") > -1 || low.indexOf("construction license") > -1) return "FOOTER";
    if (links.length >= 3 && t.length < 40) return "SOCIAL FOOTER";
    if (low.indexOf("mortgage") > -1 || low.indexOf("loan officer") > -1 || low.indexOf("nmls") > -1) return "LENNAR MORTGAGE";
    if (low.indexOf("everything's included") > -1 || low.indexOf("everythings included") > -1 || low.indexOf("see all offers") > -1 || (low.indexOf("kitchen appliances") > -1 && low.indexOf("energy") > -1)) return "EVERYTHING'S INCLUDED";
    if (low.indexOf("get directions") > -1 || low.indexOf("visit us") > -1 || /\bdirections\b/.test(low) || (/maps?\.(google|apple)|goo\.gl\/maps|\/directions/.test(html))) return "MAP / ADDRESS";
    if (low.indexOf("here to help") > -1 || low.indexOf("contact us") > -1 || low.indexOf("your new home consultant") > -1 || low.indexOf("real estate professional") > -1) return "WE'RE HERE TO HELP";
    if (low.indexOf("why buy") > -1 || low.indexOf("why lennar") > -1 || low.indexOf("our team") > -1) return "WHY BUY LENNAR";
    if (/closing cost|flex cash|flex-cash|% ?off|save \$|\$[0-9]|\bapr\b|incentive|limited[ -]time|smart ?buy|special financing|below market/.test(low)) return "OFFER / SALES MESSAGE";
    if (/explore homes/.test(low) || /sq\.? ?ft\s*\/\s*beds\s*\/\s*baths/.test(low) || (low.match(/view home\b/g) || []).length >= 2) return "HOMESITE LISTING";
    if (/\bbed(s|room)?\b|\bbath|sq\.? ?ft|square feet|floor ?plan|home ?site|priced from|starting (from|at)/.test(low)) return "FEATURED FLOORPLAN";
    if (big && t.length < 30) return "HERO IMAGE";
    /* the position test STAYS as a fallback for a first block whose mark matches neither
       rule above; what fixes SUN's case is that `logo` is now true on its own merits */
    if ((logo || MODROWS.indexOf(r) === 0) && imgs.length && !big && t.length < 160) return "LOGO HEADER";
    if (imgs.length >= 3 && !big && t.length < 140) return "AMENITY ICONS";
    if (!t && imgs.length) return big ? "HERO IMAGE" : "IMAGE";
    if (!t && !imgs.length) return "SPACER";
    if (links.length === 1 && !heads && t.length > 0 && t.length <= 26) return "BUTTON / CTA";
    if (t.length <= 30 && links.length === 0 && !big) return "EYEBROW";
    if (heads || t.length <= 55) return "HEADLINE";
    return "BODY COPY";
  }
  function secLabel(el: Element): { label: string; hero: boolean } {
    var l = labelFor(el);
    return { label: l, hero: l === "HERO IMAGE" || l === "IMAGE" };
  }
  /* the editable links of a section — monolith panel rules (2026-08-09 FULL
     11-panel capture): EVERY <a href> gets fields, capped at 8 — text links get
     text+URL, textless links (logo wrap, social icons) get URL-only (the social
     footer shows Link 1-6 URL rows under its image cards). */
  function secLinks(el: Element) {
    var links = el.querySelectorAll("a[href]"), li: { text: string; href: string }[] = [], i;
    for (i = 0; i < links.length && li.length < 8; i++) {
      var t = (links[i].textContent || "").replace(/\s+/g, " ").trim();
      li.push({ text: t && t.length < 60 ? t : "", href: links[i].getAttribute("href") || "" });
    }
    return li;
  }
  /* harvest a section for the parent inspector */
  function secInfo(el: Element) {
    var lab = secLabel(el), imgs = bigImgs(el), ii: { src: string; alt: string }[] = [], i;
    for (i = 0; i < imgs.length && ii.length < 6; i++) ii.push({ src: imgs[i].getAttribute("src") || "", alt: imgs[i].getAttribute("alt") || "" });
    return { idx: MODROWS.indexOf(el), label: lab.label, hero: lab.hero, imgs: ii, links: secLinks(el) };
  }
  function postSel(el: Element | null) {
    try { (window.parent as unknown as Record<string, (s: unknown) => void>).__edSel(el ? secInfo(el) : null); } catch (e) { /* noop */ }
    var prev = D.querySelectorAll("[data-essel]"); for (var p = 0; p < prev.length; p++) prev[p].removeAttribute("data-essel");
    if (el) el.setAttribute("data-essel", "1");
  }
  /* ── section ops the parent inspector calls (index-addressed) ── */
  function stripSrcset(img: Element) { img.removeAttribute("srcset"); img.removeAttribute("sizes"); var pic = img.closest("picture"); if (pic) { var ss = pic.querySelectorAll("source"); for (var j = 0; j < ss.length; j++) ss[j].parentNode!.removeChild(ss[j]); } }
  (W as Record<string, unknown>).__edSecImg = function (idx: number, imgIdx: number, url: string, alt?: string) {
    var el = MODROWS[idx]; if (!el) return null; var imgs = bigImgs(el); var im = imgs[imgIdx]; if (!im) return null;
    im.setAttribute("src", url); stripSrcset(im); if (alt != null) im.setAttribute("alt", alt); touched(); return secInfo(el);
  };
  (W as Record<string, unknown>).__edSecLink = function (idx: number, linkIdx: number, field: string, val: string) {
    var el = MODROWS[idx]; if (!el) return null;
    /* SAME selection rule as secLinks — the inspector's index must address the same <a> */
    var links = el.querySelectorAll("a[href]"), li: Element[] = [], i;
    for (i = 0; i < links.length && li.length < 8; i++) li.push(links[i]);
    var a = li[linkIdx]; if (!a) return null;
    if (field === "text") a.textContent = val; else a.setAttribute("href", val);
    touched(); return secInfo(el);
  };
  (W as Record<string, unknown>).__edSecRemove = function (idx: number) {
    var el = MODROWS[idx]; if (!el) return; el.parentNode!.removeChild(el); if (CUR === el) { CUR = null; ch.classList.remove("on"); } postSel(null); scan(); touched();
  };
  (W as Record<string, unknown>).__edSecInfo = function (idx: number) { var el = MODROWS[idx]; return el ? secInfo(el) : null; };
  var CUR: Element | null = null;
  function place(r: Element | null) {
    if (!r) { ch.classList.remove("on"); CUR = null; return; }
    CUR = r;
    var rect = r.getBoundingClientRect();
    var sy = W.pageYOffset as number || 0;
    ch.style.top = rect.top + sy + "px"; ch.style.left = rect.left + "px";
    ch.style.width = rect.width + "px"; ch.style.height = rect.height + "px";
    ch.classList.add("on");
  }
  function modOf(n: Node | null): Element | null {
    while (n && n !== B) {
      if ((n as Element).getAttribute && (n as Element).getAttribute("data-esmod") === "1") return n as Element;
      n = n.parentNode;
    }
    return null;
  }
  D.addEventListener("mousemove", function (e) {
    if ((e.target as Element).closest && (e.target as Element).closest(".esch")) return;
    var m = modOf(e.target as Node);
    place(m);
    if (m) ch.querySelector(".esbadge")!.textContent = secLabel(m).label;
  });
  /* click SELECTS the section → the parent renders the TEMPLATE SECTION
     inspector (monolith esInsp); clicking off-section clears the selection */
  D.addEventListener("click", function (e) {
    if ((e.target as Element).closest && (e.target as Element).closest(".esch")) return;
    postSel(modOf(e.target as Node));
  });
  function touched() { try { (window.parent as unknown as Record<string, (h: void) => void>).__edTouched(); } catch (e) { /* noop */ } }
  ch.querySelector(".esbtns")!.addEventListener("mousedown", function (e) {
    var b = (e.target as Element).closest("button");
    if (!b || !CUR) return;
    e.preventDefault(); e.stopPropagation();
    var a = b.getAttribute("data-a"), idx = MODROWS.indexOf(CUR);
    if (a === "up" && idx > 0) CUR.parentNode!.insertBefore(CUR, MODROWS[idx - 1]);
    else if (a === "down" && idx < MODROWS.length - 1) CUR.parentNode!.insertBefore(MODROWS[idx + 1], CUR);
    else if (a === "dup") CUR.parentNode!.insertBefore(CUR.cloneNode(true), CUR.nextSibling);
    else if (a === "del") { CUR.parentNode!.removeChild(CUR); CUR = null; ch.classList.remove("on"); postSel(null); }
    scan(); if (CUR) place(CUR); touched();
  });
  D.addEventListener("click", function (e) {
    var n: Node | null = e.target as Node;
    while (n && n !== D) { if ((n as Element).tagName === "A") { e.preventDefault(); break; } n = n.parentNode; }
  }, true);
  /* ── CLAMP COPY AS IT IS TYPED, AGAINST THE BLOCK'S OWN LIMIT (2026-08-31) ────
     Bound to the DOCUMENT, not to `B`: `B` is captured when this runs, and the body is
     replaced whenever the host re-renders the document — a listener sitting on a
     detached body never fires again, which is the same stale-reference shape as a tab
     still running a pre-edit page. `capFor` reads the label stamped at scan, so the
     text being capped is never also the input to the classification. */
  if (!(W as Record<string, unknown>).__esCapBound) {
  (W as Record<string, unknown>).__esCapBound = 1;
  D.addEventListener("input", function (e) {
    try {
      var r = modOf(e.target as Node);
      var cap = capFor(r);
      if (!cap || !r) { touched(); return; }
      var t = ((r as HTMLElement).innerText || "").replace(/\s+/g, " ").trim();
      if (t.length <= cap) { touched(); return; }
      /* trim from whichever element is actually being edited, so the caret stays put */
      var n = e.target && (e.target as Node).nodeType === 1 ? e.target as HTMLElement : null;
      var host = (n && n.isContentEditable) ? n : (r as HTMLElement);
      var txt = host.innerText || "";
      host.innerText = txt.slice(0, Math.max(0, txt.length - (t.length - cap)));
      capFlash("Limit reached — " + cap + " characters max");
      try {
        var sel = window.getSelection(), rg = D.createRange();
        rg.selectNodeContents(host); rg.collapse(false);
        sel!.removeAllRanges(); sel!.addRange(rg);
      } catch (err) { /* noop */ }
    } catch (err) { /* noop */ }
    touched();
  }, true);
  }
  /* insert a new block after MODROWS[idx] — table-context-safe (Gotcha #2) */
  (W as Record<string, unknown>).__edInsertAfter = function (idx: number, html: string) {
    var ref = MODROWS[idx] || MODROWS[MODROWS.length - 1];
    if (!ref) return;
    var isTR = ref.tagName === "TR" || (ref.parentNode && /^(TBODY|TABLE)$/.test((ref.parentNode as Element).tagName));
    var node: Element | null;
    if (isTR) { var tb = D.createElement("tbody"); tb.innerHTML = html; node = tb.firstElementChild; }
    else {
      var dv = D.createElement("div");
      dv.innerHTML = '<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center" style="width:600px;max-width:100%;border-collapse:collapse">' + html + "</table>";
      node = dv;
    }
    if (node) { ref.parentNode!.insertBefore(node, ref.nextSibling); scan(); touched(); }
  };
  /* image swap that respects Gotcha #1 */
  (W as Record<string, unknown>).__edSwapImg = function (oldSrcPart: string, newUrl: string) {
    var imgs = D.querySelectorAll("img");
    for (var i = 0; i < imgs.length; i++) {
      if ((imgs[i].getAttribute("src") || "").indexOf(oldSrcPart) > -1) {
        imgs[i].setAttribute("src", newUrl);
        imgs[i].removeAttribute("srcset"); imgs[i].removeAttribute("sizes");
        var pic = imgs[i].closest("picture");
        if (pic) { var ss = pic.querySelectorAll("source"); for (var j = 0; j < ss.length; j++) ss[j].parentNode!.removeChild(ss[j]); }
        touched(); return true;
      }
    }
    return false;
  };
  scan(); setTimeout(scan, 400); setTimeout(scan, 1000);
};

/**
 * TEST-ONLY HANDLE ON THE REAL EDITOR (2026-08-31).
 *
 * `EDITOR_FN` is stringified into the iframe, so nothing inside it can be imported and
 * `labelFor` cannot be unit-tested the normal way. jsdom does not execute an injected
 * srcDoc script either, so rendering the component and reading the badges does not work
 * in this harness (measured — the iframe stays unlabelled).
 *
 * It reads only `document`, `document.body` and `window`, so a test can point it at
 * jsdom's own document and assert on the badges it writes. Exported rather than
 * duplicated: a second copy of the labeler would drift from the one that ships, which
 * is the whole failure this labeler already carries scars from.
 */
export const __EDITOR_FN_FOR_TESTS = EDITOR_FN;

/** strip editor chrome for export (legacy esCleanExt/getRealTemplateHTML) */
function cleanSerialize(doc: Document): string {
  const root = doc.documentElement.cloneNode(true) as HTMLElement;
  root.querySelectorAll("[contenteditable]").forEach((el) => { el.removeAttribute("contenteditable"); el.removeAttribute("spellcheck"); });
  root.querySelectorAll("[data-esmod]").forEach((el) => el.removeAttribute("data-esmod"));
  root.querySelectorAll("[data-essel]").forEach((el) => el.removeAttribute("data-essel"));
  root.querySelectorAll(".esch,[data-esedit]").forEach((el) => el.parentNode?.removeChild(el));
  return "<!DOCTYPE html>\n" + root.outerHTML;
}

export default function TemplateEditor({ html, onSerialized, onSelect, apiRef, caps, lockArt }: Props) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    w.__edTouched = () => {
      const doc = frameRef.current?.contentDocument;
      if (doc) onSerialized(cleanSerialize(doc));
    };
    w.__edSel = (s: unknown) => { if (onSelect) onSelect(s as TplSection | null); };
    if (apiRef) {
      const fw = () => frameRef.current?.contentWindow as unknown as Record<string, (...a: unknown[]) => unknown> | null;
      apiRef.current = {
        img: (idx, imgIdx, url, alt) => (fw()?.__edSecImg?.(idx, imgIdx, url, alt) as TplSection | null) ?? null,
        link: (idx, linkIdx, field, val) => (fw()?.__edSecLink?.(idx, linkIdx, field, val) as TplSection | null) ?? null,
        remove: (idx) => { fw()?.__edSecRemove?.(idx); },
        deselect: () => { try { const d = frameRef.current?.contentDocument; d?.querySelectorAll("[data-essel]").forEach((el) => el.removeAttribute("data-essel")); } catch { /* noop */ } },
      };
    }
    return () => { delete w.__edTouched; delete w.__edSel; if (apiRef) apiRef.current = null; };
  }, [onSerialized, onSelect, apiRef]);

  /* the caps ride into the frame with the editor — JSON.stringify keeps it a literal,
     and an absent/empty map means "no limits", exactly as before this existed.
     The <scr>+<ipt> splits are deliberate: an unbroken script tag in this source would
     close the surrounding one when the file is inlined. */
  /* eslint-disable no-useless-concat */
  const inj = '<style data-esedit>body{cursor:text}</style><scr' + 'ipt data-esedit>('
    + EDITOR_FN.toString() + ')(' + JSON.stringify(caps || {}) + ',' + (lockArt ? 'true' : 'false')
    + ');</scr' + "ipt>";
  /* eslint-enable no-useless-concat */
  const srcdoc = html.indexOf("</body>") > -1 ? html.replace("</body>", inj + "</body>") : html + inj;

  /* the frame grows to the EMAIL's full height (block-mode .paper model) — the
     old viewport-tied CSS height cut the email off and made zoom-out show a
     clipped postage stamp over an empty canvas (Jack 2026-08-09: "completely
     messed up"). Synced on load, after edits, and on image loads. */
  const syncH = () => {
    const f = frameRef.current; const d = f?.contentDocument;
    if (!f || !d || !d.body) return;
    const h = Math.max(d.body.scrollHeight, d.documentElement ? d.documentElement.scrollHeight : 0);
    if (h > 100) f.style.height = h + "px";
  };
  useEffect(() => {
    const t1 = setTimeout(syncH, 150), t2 = setTimeout(syncH, 700), t3 = setTimeout(syncH, 2000);
    const iv = setInterval(syncH, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html]);

  return (
    <div className="tpledit">
      <iframe ref={frameRef} title="Template editor" srcDoc={srcdoc} className="tpledit-frame" onLoad={syncH} />
    </div>
  );
}
